import {gql} from '@apollo/client';

export const GET_ASSET_CLASS_AND_DABAPRO_DATA = gql`
  query GetAssetClassAndDabaProChartData($filters: OverviewFilters) {
    getAssetClassAndDabaProChartData(filters: $filters) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithOverviewResponse {
        data {
          ... on AssetClassAndDabaProChartData {
            assetClass {
              label
              value
            }
            dabaPro {
              label
              value
            }
          }
        }
      }
    }
  }
`;

export const GET_USER_GROWTH_STATISTICS = gql`
  query GetUserGrowthStatistics($month: Int, $year: Int) {
    getUserGrowthStatistics(month: $month, year: $year) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithOverviewResponse {
        data {
          ... on UserGrowthChartStatisticsData {
            userStatistics {
              daily {
                createdAt
                total
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_DABAPRO_GROWTH_STATISTICS = gql`
  query GetDabaProGrowthStatistics($month: Int, $year: Int) {
    getDabaProGrowthStatistics(month: $month, year: $year) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithOverviewResponse {
        data {
          ... on DabaProChartStatisticsData {
            dabaProStatistics {
              daily {
                total
                createdAt
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_CHART_USER_STATISTICS = gql`
  query GetUserStatistics($filters: OverviewFilters) {
    getUserStatistics(filters: $filters) {
      ... on Error {
        statusCode
        type
      }
      ... on ResponseWithOverviewResponse {
        message
        data {
          ... on UserStatisticsData {
            totalUsers
            totalActiveUsers
            activeInvestors
            totalFirstTimeInvestors
            totalUsersWithUserName
            usersWithDeposits {
              totalCount
              statistic {
                currency
                totalAmount
                totalFees
                symbol
              }
            }
            usersWhoMadeThierFirstDeposit {
              totalCount
              statistic {
                currency
                totalAmount
                totalFees
                symbol
              }
            }
          }
        }
      }
    }
  }
`;

export const GET_KYC_STATS = gql`
  query GetKycStatistics($filters: OverviewFilters) {
    getKycStatistics(filters: $filters) {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithOverviewResponse {
        data {
          ... on KycStatisticsData {
            kycStatus {
              status
              count
            }
            documentCheckStatus {
              status
              count
            }
            amlStatus {
              status
              count
            }
          }
        }
      }
    }
  }
`;

export const GET_FINANCIAL_STATISTICS = gql`
  query GetFinancialStatistics($filters: OverviewFilters) {
    getFinancialStatistics(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithOverviewResponse {
        data {
          ... on FinancialStatisticsData {
            withdrawals {
              statistics {
                currency
                totalAmount
                symbol
              }
              overview {
                currency
                status
                symbol
                totalAmount
                totalCount
                totalFees
              }
            }
            deposits {
              statistics {
                currency
                totalAmount
                symbol
              }
              overview {
                currency
                status
                symbol
                totalAmount
                totalCount
                totalFees
              }
            }
            assetInvestments {
              statistics {
                currency
                totalAmount
                symbol
                status
              }
              overview {
                stockExchange
                currency
                instrumentType
                symbol
                totalBuyAmount
                totalBuyCount
                totalBuyFees
                totalSellAmount
                totalSellCount
                totalSellFees
              }
            }
            revenueBreakdown {
              currency
              symbol
              transactionRevenue
              walletRevenue
              dabaProSales
              dabaAcademySales
              totalRevenue
              simulatorSales
            }
          }
        }
      }
    }
  }
`;

export const GET_INVESTOR_DISTRIBUTION_BY_REGION = gql`
  query GetInvestorDistributionByRegion($filters: OverviewFilters) {
    getInvestorDistributionByRegion(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithOverviewResponse {
        message
        data {
          ... on InvestorDistributionByRegionData {
            regions {
              region
              investorCount
              userCount
            }
          }
        }
      }
    }
  }
`;

/** Paginated users for a single region (overview modal; no extra statistics query). */
export const GET_ALL_USERS_BY_REGION_OVERVIEW = gql`
  query GetAllUsersByRegionOverview($filters: GetUserFilters) {
    getAllUsers(filters: $filters) {
      hasNextPage
      hasPrevPage
      totalDocs
      users {
        id
        firstName
        lastName
        username
        email
        phoneNumber
        countryCode
        createdAt
        profileCompletion {
          completionPercentage
        }
        regionCategory {
          region
          subRegion
        }
      }
    }
  }
`;

export const GET_REGION_USERS = gql`
  query GetRegionUsers($filters: RegionUsersFilters) {
    getRegionUsers(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithRegionUsers {
        message
        data {
          id
          firstName
          lastName
          email
          username
          phoneNumber
          countryCode
          createdAt
          profileCompletion {
            completionPercentage
          }
          investorProfile {
            isAccredited
          }
          regionCategory {
            region
            subRegion
          }
        }
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
      }
    }
  }
`;

export const GET_REGION_INVESTOR_USERS = gql`
  query GetRegionInvestorUsers($filters: RegionUsersFilters) {
    getRegionInvestorUsers(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithRegionUsers {
        message
        data {
          id
          firstName
          lastName
          email
          username
          phoneNumber
          countryCode
          createdAt
          profileCompletion {
            completionPercentage
          }
          investorProfile {
            isAccredited
          }
          regionCategory {
            region
            subRegion
          }
        }
        pagination {
          page
          limit
          pages
          total
          hasPrevPage
          hasNextPage
          previous
          next
        }
      }
    }
  }
`;

export const GET_REFERRAL_CHANNEL_STATS = gql`
  query GetReferralChannelStatistics {
    getReferralChannelStatistics {
      ... on Error {
        statusCode
        message
      }
      ... on ResponseWithOverviewResponse {
        data {
          ... on ReferralChannelStatisticsData {
            statistics {
              label
              value
            }
          }
        }
      }
    }
  }
`;

export const DABA_PRO_SUBSCRIPTION_STATISTICS = gql`
  query GetDabaProSubscriptionStatistics($filters: OverviewFilters) {
    getDabaProSubscriptionStatistics(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithOverviewResponse {
        message
        data {
          ... on DabaProSubscriptionStatisticsData {
            totalRevenue {
              totalAmount
              symbol
              currency
            }
            totalCount
            totalSoldUnits
            totalActive
            totalInactive
          }
        }
      }
    }
  }
`;

export const DABA_PRO_SUBSCRIPTION_MONTHLY_BREAKDOWN = gql`
  query GetDabaProSubscriptionMonthlyBreakdown($year: Int) {
    getDabaProSubscriptionMonthlyBreakdown(year: $year) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithOverviewResponse {
        message
        data {
          ... on DabaSubscriptionMonthlyBreakdownData {
            year
            breakdown {
              month
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const GET_DABA_PRO_MEMBERS = gql`
  query GetDabaProMembers($filters: DabaProMembersFilters) {
    getDabaProMembers(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithDabaSubscriptionMembers {
        message
        data {
          user {
            id
            firstName
            email
            lastName
            imageUrl
            currentSessionDetails {
              currentDateTime
            }
            investorProfile {
              identityCard {
                kycStatus
              }
            }
          }
          startDate
          endDate
          isRecurring
          subscriptionType
          hasInvested
          hasPortfolio
          subscriptionStatus
        }
        pagination {
          hasNextPage
          hasPrevPage
          next
          page
          previous
          total
        }
      }
    }
  }
`;

export const DABA_ACADEMY_SUBSCRIPTION_STATISTICS = gql`
  query GetDabaAcademySubscriptionStatistics($filters: OverviewFilters) {
    getDabaAcademySubscriptionStatistics(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithOverviewResponse {
        message
        data {
          ... on DabaAcademySalesStatisticsData {
            totalRevenueSelectedPeriod {
              currency
              totalAmount
              symbol
            }
            totalRevenueSinceLaunch {
              currency
              totalAmount
              symbol
            }
            totalMembersSinceLaunch
          }
        }
      }
    }
  }
`;

export const DABA_ACADEMY_SUBSCRIPTION_MONTHLY_BREAKDOWN = gql`
  query GetDabaAcademySubscriptionMonthlyBreakdown($year: Int) {
    getDabaAcademySubscriptionMonthlyBreakdown(year: $year) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithOverviewResponse {
        message
        data {
          ... on DabaSubscriptionMonthlyBreakdownData {
            year
            breakdown {
              month
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const DABA_ACADEMY_MEMBERS = gql`
  query GetDabaAcademyMembers($filters: DabaAcademyFilters) {
    getDabaAcademyMembers(filters: $filters) {
      ... on Error {
        message
        statusCode
        type
        errorTypeEnum
        retryAfter
      }
      ... on ResponseWithDabaSubscriptionMembers {
        message
        data {
          user {
            id
            firstName
            email
            lastName
            currentSessionDetails {
              currentDateTime
            }
          }
          startDate
          endDate
          hasInvested
          hasPortfolio
          purchaseDate
          lastInvestmentDate
        }
        pagination {
          hasNextPage
          hasPrevPage
          next
          page
          previous
          total
        }
      }
    }
  }
`;
